import type { ReactNode } from 'react';
import { TONE, type Tone } from './tone';

// Phase 36 §21b — notices.
//
// Four tones, one shape. A notice lives INSIDE the card it concerns, as a
// grouped surface — wash, border, no shadow of its own — never as a fifth
// floating layer above the page. A warning that scrolls away inside a grid is
// a warning nobody reads, so it is always a sibling of the content rather than
// a row in it.
//
// It must never render a zero. `0 articles invisible to the AI` is furniture:
// it occupies the place a real warning would appear and teaches the reader to
// skip that spot. Absent is the correct rendering of nothing being wrong —
// `hasFinding()` in ./tone is the guard.

/** `neutral` is deliberately absent: a notice with no consequence is not one. */
export type NoticeTone = Exclude<Tone, 'neutral'>;

export function Notice({
  tone,
  headline,
  body,
  action,
}: {
  tone: NoticeTone;
  headline: string;
  body?: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  const t = TONE[tone];
  return (
    <div
      className="flex flex-none items-start gap-3 rounded-[10px] border px-[18px] py-[14px]"
      style={{ borderColor: t.line, background: t.wash }}
    >
      <div
        className="mt-[1px] flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md text-[13px] font-bold text-[var(--co-ink-invert)]"
        style={{ background: t.ink }}
        aria-hidden="true"
      >
        {tone === 'ok' ? '✓' : '!'}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="co-mono text-[10px] font-semibold uppercase tracking-[.14em]"
          style={{ color: t.ink }}
        >
          {headline}
        </div>
        {body && (
          <p
            className="mt-[5px] max-w-[92ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]"
            style={{ textWrap: 'pretty' } as React.CSSProperties}
          >
            {body}
          </p>
        )}
      </div>
      {/* The action sits in the notice because a fix has to be reachable from
          where it was diagnosed. Sending the reader to another screen to act on
          what they were just told is how a warning becomes permanent. */}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="co-mono flex-none whitespace-nowrap rounded-lg border bg-[var(--co-raised)] px-3 py-[7px] text-[10px] font-semibold uppercase tracking-[.1em]"
          style={{ borderColor: t.line, color: t.ink }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
